import {
  TTORIA_ASYNC_FEEDBACK_EVENT,
  TTORIA_ASYNC_FEEDBACK_OP_CLASSES,
  TTORIA_ASYNC_FEEDBACK_V1,
  resolveTtoriaAsyncFeedbackConfig
} from './contract.js';

const OUTCOMES = new Set(['succeeded', 'failed', 'cancelled', 'stale-discarded']);
const ERROR_CLASSES = new Set([
  'none',
  'validation',
  'authentication',
  'authorization',
  'conflict',
  'rate-limit',
  'offline',
  'timeout',
  'unavailable',
  'unknown'
]);
const DURATION_BUCKETS = new Set(['lt-100ms', '100-999ms', '1-9s', '10-29s', '30s-plus']);
const FEEDBACK_BUCKETS = new Set(['same-frame', 'lt-100ms', '100-999ms', 'not-observed']);
const RAIL_DWELL_BUCKETS = new Set(['not-shown', 'lt-1s', '1-9s', '10-29s', '30s-plus']);
const NETWORK_CLASSES = new Set(['none', 'offline', 'online']);
const RECONCILE_CLASSES = new Set(['none', 'not-required', 'required', 'completed', 'failed']);
const RETRY_CLASSES = new Set(['safe-read', 'domain-idempotent', 'reconcile-only']);

function clean(value = '') {
  return String(value ?? '').trim();
}

function exactBoolean(value) {
  return value === true;
}

function oneOf(value, allowed, fallback) {
  const normalized = clean(value);
  return allowed.has(normalized) ? normalized : fallback;
}

function safeActionId(value = '') {
  const normalized = clean(value);
  return /^[a-z0-9][a-z0-9._:-]{0,199}$/iu.test(normalized) ? normalized : '';
}

export function sanitizeTtoriaAsyncFeedbackEvent(raw = {}, serverIdentity = {}) {
  const source = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  const identity = resolveTtoriaAsyncFeedbackConfig(serverIdentity);
  if (!identity.enabled || identity.standard !== TTORIA_ASYNC_FEEDBACK_V1) return null;
  const actionId = safeActionId(source.actionId);
  const operationClass = oneOf(source.operationClass, new Set(TTORIA_ASYNC_FEEDBACK_OP_CLASSES), '');
  const outcome = oneOf(source.outcome, OUTCOMES, '');
  if (!actionId || !operationClass || !outcome) return null;
  return Object.freeze({
    event: TTORIA_ASYNC_FEEDBACK_EVENT,
    appId: identity.appId,
    target: identity.target,
    buildId: identity.buildId,
    artifactSha256: identity.artifactSha256,
    actionId,
    operationClass,
    outcome,
    errorClass: oneOf(source.errorClass, ERROR_CLASSES, outcome === 'failed' ? 'unknown' : 'none'),
    durationBucket: oneOf(source.durationBucket, DURATION_BUCKETS, '30s-plus'),
    firstFeedbackBucket: oneOf(source.firstFeedbackBucket, FEEDBACK_BUCKETS, 'not-observed'),
    railShown: exactBoolean(source.railShown),
    railDwellBucket: oneOf(source.railDwellBucket, RAIL_DWELL_BUCKETS, 'not-shown'),
    duplicateSuppressed: exactBoolean(source.duplicateSuppressed),
    staleDiscarded: exactBoolean(source.staleDiscarded),
    cancelRequested: exactBoolean(source.cancelRequested),
    cancelConfirmed: exactBoolean(source.cancelConfirmed),
    offlineClass: oneOf(source.offlineClass, NETWORK_CLASSES, 'none'),
    reconcileClass: oneOf(source.reconcileClass, RECONCILE_CLASSES, 'none'),
    retryClass: oneOf(source.retryClass, RETRY_CLASSES, 'reconcile-only'),
    legacyLoaderViolation: exactBoolean(source.legacyLoaderViolation)
  });
}

export function collectTtoriaAsyncFeedbackTelemetryBatch(rawBatch = [], {
  serverIdentity = {},
  log = (...args) => console.log(...args),
  maxBatchSize = 50
} = {}) {
  if (!Array.isArray(rawBatch) || rawBatch.length === 0 || rawBatch.length > maxBatchSize) {
    return Object.freeze({ accepted: 0, rejected: Array.isArray(rawBatch) ? rawBatch.length : 1 });
  }
  let accepted = 0;
  let rejected = 0;
  for (const candidate of rawBatch) {
    const event = sanitizeTtoriaAsyncFeedbackEvent(candidate, serverIdentity);
    if (!event) {
      rejected += 1;
      continue;
    }
    log(JSON.stringify(event));
    accepted += 1;
  }
  return Object.freeze({ accepted, rejected });
}

export function createTtoriaAsyncFeedbackTelemetryBatcher({
  send,
  enabled = false,
  maxBatchSize = 20
} = {}) {
  const pending = [];
  let flushPromise = null;
  const flush = async () => {
    if (!enabled || typeof send !== 'function' || pending.length === 0) return false;
    if (flushPromise) return flushPromise;
    flushPromise = Promise.resolve()
      .then(async () => {
        let delivered = false;
        while (pending.length > 0) {
          const batch = pending.splice(0, Math.max(1, maxBatchSize));
          try {
            await send(batch);
            delivered = true;
          } catch {
            return false;
          }
        }
        return delivered;
      })
      .finally(() => {
        flushPromise = null;
        if (pending.length > 0) queueMicrotask(() => { void flush(); });
      });
    return flushPromise;
  };
  return Object.freeze({
    record(event = {}) {
      if (!enabled || !event || typeof event !== 'object') return false;
      pending.push({ ...event });
      if (pending.length >= Math.max(1, maxBatchSize)) void flush();
      return true;
    },
    flush,
    pendingCount() {
      return pending.length;
    }
  });
}
